/**
 * Score `text` against a fuzzy `query` (case-insensitive subsequence). Returns
 * `null` when the query's characters don't all appear in order, else a score
 * where consecutive matches count double, so tighter matches rank higher. An
 * empty query scores `0` (everything passes, original order preserved).
 *
 * Uses DP to find the *highest-scoring* alignment, not just the first — a greedy
 * scan would miss a tighter consecutive run later in the text (e.g. "ab" in
 * "a_ab" should score the consecutive run, not the leading scattered one).
 */
export function fuzzyRank(query: string, text: string): number | null {
  if (!query) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const m = q.length;
  const n = t.length;
  if (m > n) return null;

  const NEG = -Infinity;
  // Rolling over text positions: g[j] = best score matching q[0..j) so far;
  // f[j] = best where the last matched text char is the current one (so the next
  // adjacent match earns the +2 consecutive bonus).
  let g = new Array<number>(m + 1).fill(NEG);
  let f = new Array<number>(m + 1).fill(NEG);
  // Scratch rows reused across text positions (swap-and-refill) instead of allocating
  // two arrays per character — bestFuzzyRank runs this per label+keyword per keystroke.
  let ng = new Array<number>(m + 1);
  let nf = new Array<number>(m + 1);
  g[0] = 0;
  for (let ti = 0; ti < n; ti++) {
    ng.fill(NEG);
    nf.fill(NEG);
    ng[0] = 0; // matching zero query chars is always free
    for (let j = 1; j <= m; j++) {
      if (t[ti] === q[j - 1]) {
        const start = g[j - 1] === NEG ? NEG : g[j - 1] + 1;
        const consecutive = f[j - 1] === NEG ? NEG : f[j - 1] + 2;
        nf[j] = Math.max(start, consecutive);
      }
      ng[j] = Math.max(g[j], nf[j]);
    }
    // This position's rows become the previous rows for the next iteration.
    [g, ng] = [ng, g];
    [f, nf] = [nf, f];
  }
  return g[m] === NEG ? null : g[m];
}

/** Best fuzzy score of `query` across a label and its keywords, or `null` if none match. */
export function bestFuzzyRank(
  query: string,
  label: string,
  keywords: string[] = []
): number | null {
  let best: number | null = null;
  for (const text of [label, ...keywords]) {
    const rank = fuzzyRank(query, text);
    if (rank !== null && (best === null || rank > best)) best = rank;
  }
  return best;
}
